// FILAS DE ATENDIMENTO (/v1/queues): a fila para onde uma chamada RECEBIDA cai
// e os membros que a atendem — estratégia de distribuição, tempo de toque,
// pausa pós-atendimento e teto de espera.
//
// Não confundir com ./queue-config.js, que apesar do nome parecido é o outro
// sentido da ligação: lá é a regra da fila do DISCADOR (a ordem em que os leads
// serão CHAMADOS, peso por vendedor e cadência de retry, uma configuração só
// por organização). Aqui são N filas nomeadas, cada uma com sua lista de
// membros, para a chamada que ENTRA. Os dois módulos não se importam.
//
// Hoje isto é só CONFIG: o roteamento ao vivo depende do módulo de filas do PBX
// (app_queue realtime na VPS), ainda não ativo. Ver
// docs/placeholders/filas-de-atendimento.md. A tela é honesta quanto a isso e
// não simula monitoramento em tempo real.
//
// O MEMBRO da fila é um usuário da org, mas a tela só oferece quem tem ramal
// provisionado (ver ./sip.js) — atender uma fila sem ramal não existe. Esse
// cruzamento acontece na TELA, com duas consultas; nenhum dos dois módulos
// precisa conhecer o outro.
import { api, toApiError } from "./client.js";

export type QueueStrategy = "ringall" | "leastrecent" | "fewestcalls" | "random" | "rrmemory";

export interface Queue {
  id: string;
  organizationId: string;
  name: string;
  strategy: QueueStrategy;
  timeout: number;
  wrapUpTime: number;
  maxWaiting: number;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface QueueMember {
  id: string;
  organizationId: string;
  queueId: string;
  userId: string;
  penalty: number;
  createdAt: string;
}

export async function getQueues(): Promise<Queue[]> {
  const { data, error } = await api<{ queues: Queue[] }>("/v1/queues");
  if (error) throw toApiError("GET /v1/queues", error);
  return data.queues;
}

export async function createQueue(input: {
  name: string;
  strategy?: QueueStrategy;
  timeout?: number;
  wrapUpTime?: number;
  maxWaiting?: number;
}): Promise<Queue> {
  const { data, error } = await api<Queue>("/v1/queues", { method: "POST", body: input });
  if (error) throw toApiError("POST /v1/queues", error);
  return data;
}

export async function updateQueue(
  id: string,
  patch: Partial<{
    name: string;
    strategy: QueueStrategy;
    timeout: number;
    wrapUpTime: number;
    maxWaiting: number;
  }>,
): Promise<Queue> {
  const { data, error } = await api<Queue>(`/v1/queues/${id}`, { method: "PATCH", body: patch });
  if (error) throw toApiError("PATCH /v1/queues", error);
  return data;
}

export async function deleteQueue(id: string): Promise<void> {
  const { error } = await api(`/v1/queues/${id}`, { method: "DELETE" });
  if (error) throw toApiError("DELETE /v1/queues", error);
}

export async function getQueueMembers(queueId: string): Promise<QueueMember[]> {
  const { data, error } = await api<{ members: QueueMember[] }>(`/v1/queues/${queueId}/members`);
  if (error) throw toApiError("GET /v1/queues/:id/members", error);
  return data.members;
}

export async function addQueueMember(
  queueId: string,
  input: { userId: string; penalty?: number },
): Promise<QueueMember> {
  const { data, error } = await api<QueueMember>(`/v1/queues/${queueId}/members`, {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/queues/:id/members", error);
  return data;
}

export async function removeQueueMember(queueId: string, memberId: string): Promise<void> {
  const { error } = await api(`/v1/queues/${queueId}/members/${memberId}`, { method: "DELETE" });
  if (error) throw toApiError("DELETE /v1/queues/:id/members/:memberId", error);
}
